import Chart from "pro-features/components/Chart/src/Chart";
import type React from "react";
import { useMemo, useRef } from "react";
import { PixelPlaceholderLoader } from "@similarweb/ui-components";
import { ChartContainer } from "./StyledComponents";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import { colorsPalettes } from "@similarweb/styles";
import { minJourneyVisitsAbbrFilter } from "app/pages/segment-user-flow/buyer-journey/SegmentUserFlowBucketTableColumnsConfig";
import { Interactions, SEGMENT_MIN_VISITS_THRESHOLD } from "app/pages/segment-user-flow/buyer-journey/Interactions";
import { percentageFilter } from "app/filters/ngFilters";

const TooltipContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-family: Roboto;
    font-weight: 400;
    fill: ${colorsPalettes.carbon[500]};
    .circle {
        font-size: 20px;
        font-weight: 600;
    }
    .incoming-traffic {
        fill: ${colorsPalettes.blue[500]};
        .circle {
            fill: ${colorsPalettes.blue[500]};
        }
        .traffic-values {
            font-weight: 500;
        }
    }
    .outgoing-traffic {
        .traffic-values {
            font-weight: 500;
        }
    }
    .node-url {
        font-weight: 400;
        fill: ${colorsPalettes.carbon[300]};
    }
`;

function chunkString(str, length) {
    return str.match(new RegExp(".{1," + length + "}", "g"));
}

const SankeyNodeTooltipContent = (props: {
    incomingTrafficVisits: { sum: number; ratio: number };
    outGoingTrafficVisits: { sum: number; ratio: number };
    name: string;
}) => {
    const { name } = props;
    const hasBoth = props.incomingTrafficVisits?.sum && props.outGoingTrafficVisits?.sum;
    const charBreakLimit = hasBoth ? 45 : 34;
    const nameChunks = chunkString(name, charBreakLimit);
    const outgoingTrafficXOffset = hasBoth ? 140 : 10;
    const outgoingTrafficYOffset = hasBoth || nameChunks.length > 1 ? nameChunks.length + 1 + "em" : "2em";
    return (
        <TooltipContainer>
            <tspan
                x="10"
                className={"node-url-container"}
            >
                {nameChunks.map((chunk, index) => {
                    const dy = index > 0 ? "1.1em" : "";
                    return (
                        <tspan
                            x="10"
                            dy={dy}
                            className={"node-url"}
                        >
                            {chunk}
                        </tspan>
                    );
                })}
            </tspan>
            {props.incomingTrafficVisits?.sum ? (
                <tspan
                    y={outgoingTrafficYOffset}
                    className={"incoming-traffic"}
                >
                    <tspan
                        x="10"
                        dy="1.5em"
                    >
                        Incoming traffic
                    </tspan>
                    <tspan
                        className={"traffic-values"}
                        x="10"
                        dy="1.5em"
                    >
                        <tspan>{minJourneyVisitsAbbrFilter()(props.incomingTrafficVisits.sum)}</tspan>
                        {props.incomingTrafficVisits.sum > SEGMENT_MIN_VISITS_THRESHOLD ? (
                            <>
                                <tspan
                                    dy="-4px"
                                    dx="0.5em"
                                    className={"circle"}
                                >
                                    .
                                </tspan>
                                <tspan
                                    dx="0.5em"
                                    dy="4px"
                                >
                                    {percentageFilter()(props.incomingTrafficVisits.ratio || 0, 2) + "%"}
                                </tspan>
                            </>
                        ) : null}
                    </tspan>
                </tspan>
            ) : null}
            {props.outGoingTrafficVisits?.sum ? (
                <tspan
                    y={outgoingTrafficYOffset}
                    className={"outgoing-traffic"}
                >
                    <tspan
                        dy="1.5em"
                        x={outgoingTrafficXOffset}
                    >
                        Outgoing traffic
                    </tspan>
                    <tspan
                        className={"traffic-values"}
                        x={outgoingTrafficXOffset}
                        dy="1.5em"
                    >
                        <tspan>{minJourneyVisitsAbbrFilter()(props.outGoingTrafficVisits.sum)}</tspan>
                        {props.outGoingTrafficVisits.sum > SEGMENT_MIN_VISITS_THRESHOLD ? (
                            <>
                                <tspan
                                    dy="-4px"
                                    dx="0.5em"
                                    className={"circle"}
                                >
                                    .
                                </tspan>
                                <tspan
                                    dx="0.5em"
                                    dy="4px"
                                >
                                    {percentageFilter()(props.outGoingTrafficVisits.ratio || 0, 2) + "%"}
                                </tspan>
                            </>
                        ) : null}
                    </tspan>
                </tspan>
            ) : null}
        </TooltipContainer>
    );
};

const SankeyPathTooltipContent = (props: { weight: number; ratio: number }) => {
    const { ratio, weight } = props;
    return (
        <TooltipContainer>
            <tspan className={"outgoing-traffic"}>
                <tspan x="10">Traffic</tspan>
                <tspan
                    className={"traffic-values"}
                    x="10"
                    dy="1.5em"
                    fontWeight="bold"
                >
                    <tspan>{minJourneyVisitsAbbrFilter()(weight)}</tspan>
                    {weight > SEGMENT_MIN_VISITS_THRESHOLD ? (
                        <>
                            <tspan
                                dy="-4px"
                                dx="0.5em"
                                className={"circle"}
                            >
                                .
                            </tspan>
                            <tspan
                                dx="0.5em"
                                dy="4px"
                            >
                                {percentageFilter()(ratio || 0, 2) + "%"}
                            </tspan>
                        </>
                    ) : null}
                </tspan>
            </tspan>
        </TooltipContainer>
    );
};

interface IFlowChartContainer {
    width?: number;
    graphData: any[];
    isLoading: boolean;
    seriesName: string;
    interactionsMeta: any;
}

export const FlowChartContainer: React.FC<IFlowChartContainer> = (props) => {
    const { isLoading, graphData, seriesName, interactionsMeta } = props;
    const nodes = useMemo(() => {
        if (isLoading || !graphData) {
            return undefined;
        }
        const dedupNodes = {};
        let columnMinValue = Object.keys(interactionsMeta).length;
        graphData.map((nodeArr) => {
            columnMinValue = Math.min(columnMinValue, parseInt(nodeArr[0].slice(nodeArr[0].length - 1)));
            const fromNode = nodeArr[0].slice(nodeArr[0].length - 1, nodeArr[0].length);
            const toNode = nodeArr[1].slice(nodeArr[1].length - 1, nodeArr[1].length);
            dedupNodes[nodeArr[0]] = {
                id: nodeArr[0],
                name: nodeArr[0].slice(0, nodeArr[0].length - 2),
                color: Interactions[`B${fromNode}`].nodeColor,
                column: parseInt(nodeArr[0].slice(nodeArr[0].length - 1)),
            };
            dedupNodes[nodeArr[1]] = {
                id: nodeArr[1],
                name: nodeArr[1].slice(0, nodeArr[1].length - 2),
                color: Interactions[`B${toNode}`] ? Interactions[`B${toNode}`].nodeColor : colorsPalettes.blue[200],
                column: parseInt(nodeArr[1].slice(nodeArr[1].length - 1)),
            };
        });

        //normalize the column prop and fix nodes coloring
        const dedupNodesNormalizedColumn = Object.values(dedupNodes).map((node: any) => {
            return { ...node, column: node.column - columnMinValue };
        });
        return dedupNodesNormalizedColumn;
    }, [isLoading, graphData]);
    const chartRef = useRef(undefined);
    const chartConfig = useMemo(() => {
        if (!nodes) {
            return undefined;
        }
        return {
            chart: {
                height: 450,
                width: chartRef?.current?.offsetWidth - 100 || 1000,
                type: "sankey",
                spacingTop: 10,
                plotBackgroundColor: "transparent",
                events: {},
            },
            accessibility: {
                point: {
                    // valueDescriptionFormat: "{index}. {point.from} to {point.to}, {point.weight}.",
                },
            },
            series: [
                {
                    keys: ["from", "to", "weight", "ratio"],
                    data: graphData,
                    nodes: nodes,
                    nodePadding: 15,
                    type: "sankey",
                    curveFactor: 0.2,
                    centerInCategory: true,
                    states: {
                        hover: {
                            linkOpacity: 0.8,
                        },
                        inactive: {
                            linkOpacity: 0.2,
                        },
                    },
                    dataLabels: {
                        allowOverlap: false,
                        borderRadius: 2,
                    },
                },
            ],
            plotOptions: {
                series: {
                    name: seriesName,
                    connectNulls: false,
                    stickyTracking: true,
                    color: colorsPalettes.carbon[100],
                    centerInCategory: true,
                },
                sankey: {
                    tooltip: {
                        backgroundColor: colorsPalettes.carbon[0],
                        headerFormat: ``,
                        pointFormatter() {
                            const { ratio, weight } = this.options;
                            return ReactDOMServer.renderToString(
                                <SankeyPathTooltipContent
                                    weight={weight}
                                    ratio={ratio}
                                />,
                            );
                        },
                        nodeFormatter() {
                            const incomingTrafficVisits = this.linksTo
                                ? this.linksTo.reduce(
                                      (value, { weight, ratio }) => {
                                          value.sum += weight;
                                          value.ratio += ratio;
                                          return value;
                                      },
                                      { sum: 0, ratio: 0 },
                                  )
                                : undefined;
                            const outGoingTrafficVisits = this.linksFrom
                                ? this.linksFrom.reduce(
                                      (value, { weight, ratio }) => {
                                          value.sum += weight;
                                          value.ratio += ratio;
                                          return value;
                                      },
                                      { sum: 0, ratio: 0 },
                                  )
                                : undefined;
                            return ReactDOMServer.renderToString(
                                <SankeyNodeTooltipContent
                                    incomingTrafficVisits={incomingTrafficVisits}
                                    outGoingTrafficVisits={outGoingTrafficVisits}
                                    name={this.name}
                                />,
                            );
                        },
                    },
                },
            },
        };
    }, [nodes, chartRef]);
    return (
        <ChartContainer ref={chartRef}>
            {isLoading ? (
                <PixelPlaceholderLoader
                    width="100%"
                    height="360px"
                />
            ) : (
                <Chart
                    type="sankey"
                    config={chartConfig}
                    data={graphData}
                    isFinalConfig={true}
                />
            )}
        </ChartContainer>
    );
};
